import { useCallback, useEffect, useMemo, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { Config } from "@/api/models"

import {
  buildClientInfo,
  type FeedbackContext,
  type FeedbackSubmitResponse,
  submitFeedback,
  uploadFeedbackEvidence,
} from "@/api/feedback-api"
import { MessageSquare, Minimize2 } from "@/components/icons"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { getLastCorrelationId } from "@/lib/api-client"
import { queryKeys } from "@/lib/query-keys"

import { captureScreen, personalContentOnScreen } from "./capture-screen"
import {
  DEFAULT_INCIDENT,
  DEFAULT_RESPONSE_FEEDBACK,
  makeDefaultBug,
  snapshotIdentity,
} from "./defaults"
import { type EvidenceState, EvidenceUpload } from "./evidence-upload"
import { ResumeChip } from "./resume-chip"
import {
  type BugReportFields,
  type IncidentFields,
  type Lane,
  type ResponseFeedbackFields,
} from "./schemas"
import { ScreenshotAttachment } from "./screenshot-attachment"
import { BugReportStep } from "./steps/bug-report-step"
import { ConfirmationStep } from "./steps/confirmation-step"
import { IncidentStep } from "./steps/incident-step"
import { LanePicker } from "./steps/lane-picker"
import { ResponseFeedbackStep } from "./steps/response-feedback-step"
import { ReviewStep } from "./steps/review-step"

type Step = "lane" | "fields" | "review" | "done"

export type PrefillContext = {
  errorId?: string | null
  sessionId?: string | null
  messageId?: string | null
  responseContent?: string | null
  /**
   * Correlation id of the errored response that triggered this report, when the
   * caller has it. Lets support pull the matching server logs (#2152). Falls
   * back to the last-seen response correlation id when not provided.
   */
  correlationId?: string | null
}

export type FeedbackDialogProps = {
  initialLane?: Lane
  prefillContext?: PrefillContext
  /** Initial sentiment when initialLane is "response_feedback". */
  initialSentiment?: ResponseFeedbackFields["sentiment"]
  /** When true, hide the lane picker and lock the wizard to `initialLane`. */
  lockLane?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
  trigger?: React.ReactNode
  hideTrigger?: boolean
}

/** In-memory captured screenshot awaiting user consent + upload. */
type ScreenshotState = {
  blob: Blob
  previewUrl: string
  /** Populated after upload; empty until then. */
  uploadedBlobPath: string | null
}

export function FeedbackDialog({
  initialLane,
  prefillContext,
  initialSentiment,
  lockLane = false,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  trigger,
  hideTrigger = false,
}: FeedbackDialogProps) {
  const { t } = useTranslation()
  const [internalOpen, setInternalOpen] = useState(false)
  const open = controlledOpen ?? internalOpen
  const setOpen = controlledOnOpenChange ?? setInternalOpen

  const [step, setStep] = useState<Step>(initialLane ? "fields" : "lane")
  const [lane, setLane] = useState<Lane | undefined>(initialLane)

  const [responseFeedback, setResponseFeedback] = useState<ResponseFeedbackFields>(() => ({
    ...DEFAULT_RESPONSE_FEEDBACK,
    sentiment: initialSentiment ?? DEFAULT_RESPONSE_FEEDBACK.sentiment,
  }))
  const [bug, setBug] = useState<BugReportFields>(() => makeDefaultBug())
  const [incident, setIncident] = useState<IncidentFields>(DEFAULT_INCIDENT)

  const [evidence, setEvidence] = useState<EvidenceState | null>(null)

  // Auto-captured screenshot of the user's screen. Captured when the dialog
  // opens for bug / incident lanes, shown with a preview and a checkbox so
  // the user can opt out of sending it. Uploaded only at submit time if
  // `includeScreenshot` is still true.
  const [screenshot, setScreenshot] = useState<ScreenshotState | null>(null)
  const [includeScreenshot, setIncludeScreenshot] = useState(true)
  // True when the screen showed an answer built from personal sources at open time: no screenshot is
  // taken then (evidence blobs never hold personal content).
  const [screenshotSuppressed, setScreenshotSuppressed] = useState(false)

  // User's consent to escalate an eligible report to support. Default is
  // lane-dependent: incidents default on (opt out), bugs default off (opt
  // in) — see the escalation-toggle design. Only meaningful when the current
  // lane/fields combination is actually eligible; ignored otherwise.
  const [escalationRequested, setEscalationRequested] = useState(initialLane === "incident")

  const [submitting, setSubmitting] = useState(false)
  const [submitResult, setSubmitResult] = useState<FeedbackSubmitResponse | null>(null)

  // "Minimized" hides the dialog UI without resetting the wizard so the user
  // can go grab a screenshot / inspect the broken thing / copy an error, then
  // resume where they left off. A floating chip acts as the resume affordance.
  const [minimized, setMinimized] = useState(false)

  // When the dialog re-opens with new props (e.g. error boundary fires), reset
  // the wizard state so previous interaction doesn't leak in.
  const resetState = useCallback(() => {
    setStep(initialLane ? "fields" : "lane")
    setLane(initialLane)
    setResponseFeedback({
      ...DEFAULT_RESPONSE_FEEDBACK,
      sentiment: initialSentiment ?? DEFAULT_RESPONSE_FEEDBACK.sentiment,
    })
    setBug(makeDefaultBug())
    setIncident(DEFAULT_INCIDENT)
    setEvidence(null)
    // Revoke any pending object URL to avoid leaking buffers between sessions.
    setScreenshot((prev) => {
      if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl)
      return null
    })
    setIncludeScreenshot(true)
    setScreenshotSuppressed(false)
    setEscalationRequested(initialLane === "incident")
    setSubmitting(false)
    setSubmitResult(null)
    setMinimized(false)
  }, [initialLane, initialSentiment])

  useEffect(() => {
    if (!open && !minimized) {
      // Slight delay would be ideal to let close animation finish; keep simple
      // and reset immediately on close. Skip when the user minimised — state
      // is intentionally preserved until they explicitly close or submit.
      resetState()
    }
  }, [open, minimized, resetState])

  // Trigger the auto screenshot as soon as the dialog opens for a lane that
  // can attach one. We render the dialog with `data-screenshot-skip` so the
  // capture filter leaves our overlay out. Run only once per open per lane
  // — if the user toggles lanes we re-capture (different lane, different
  // context, but current screen is still the same so this is belt+braces).
  useEffect(() => {
    if (!open) return
    if (lane !== "bug" && lane !== "incident") return
    if (screenshot || screenshotSuppressed) return

    let cancelled = false
    // Defer one frame so the dialog's appearance animation lands first and
    // the portal is marked with role="dialog" in the DOM — the capture
    // filter needs that to exclude our own UI chrome.
    const id = window.requestAnimationFrame(() => {
      if (personalContentOnScreen()) {
        setScreenshotSuppressed(true)
        return
      }
      void captureScreen().then((result) => {
        if (cancelled || !result) return
        setScreenshot({
          blob: result.blob,
          previewUrl: result.previewUrl,
          uploadedBlobPath: null,
        })
      })
    })

    return () => {
      cancelled = true
      window.cancelAnimationFrame(id)
    }
  }, [open, lane, screenshot, screenshotSuppressed])

  // Snapshot whatever the frontend has locally — the `/api/config` response
  // the app is using right now + the identity claims the user menu shows —
  // so the support engineer can reproduce the user's environment without
  // asking follow-up questions.
  const queryClient = useQueryClient()
  const apiContext: FeedbackContext = useMemo(
    () => ({
      error_id: prefillContext?.errorId ?? null,
      session_id: prefillContext?.sessionId ?? null,
      message_id: prefillContext?.messageId ?? null,
      response_content: prefillContext?.responseContent ?? null,
      // Prefer the triggering error's correlation id; otherwise the last response
      // the app saw. Backend also recovers it from the error log via error_id, so
      // this is best-effort enrichment, not the only path (#2152).
      correlation_id: prefillContext?.correlationId ?? getLastCorrelationId() ?? null,
      // `open` is intentionally part of the dependency list so that each
      // fresh dialog session rebuilds the client_info snapshot.
      client_info: typeof window !== "undefined" ? buildClientInfo() : undefined,
      app_config_snapshot: queryClient.getQueryData<Config>(queryKeys.config) ?? null,
      identity_snapshot: snapshotIdentity(),
    }),
    [prefillContext, open, queryClient]
  )

  // Build the fields object for the active lane. For bug/incident lanes the
  // evidence + screenshot blob paths are injected from dialog-level state so
  // the lane step doesn't need to know about attachment handling.
  const currentFields: Record<string, unknown> | null = useMemo(() => {
    if (lane === "response_feedback") return responseFeedback as unknown as Record<string, unknown>
    const screenshotBlobPath =
      includeScreenshot && screenshot?.uploadedBlobPath ? screenshot.uploadedBlobPath : ""
    if (lane === "bug") {
      return {
        ...bug,
        evidenceBlobPath: evidence?.blobPath ?? "",
        screenshotBlobPath,
      } as unknown as Record<string, unknown>
    }
    if (lane === "incident") {
      return {
        ...incident,
        evidenceBlobPath: evidence?.blobPath ?? "",
        screenshotBlobPath,
      } as unknown as Record<string, unknown>
    }
    return null
  }, [lane, responseFeedback, bug, incident, evidence, includeScreenshot, screenshot])

  const evidencePreview = useMemo(
    () => (evidence ? { name: evidence.fileName, previewUrl: evidence.previewUrl } : null),
    [evidence]
  )

  const screenshotPreview = useMemo(
    () =>
      includeScreenshot && screenshot
        ? { name: "screen-capture.jpg", previewUrl: screenshot.previewUrl }
        : null,
    [includeScreenshot, screenshot]
  )

  const handleLaneSelect = (selectedLane: Lane) => {
    setLane(selectedLane)
    setEscalationRequested(selectedLane === "incident")
    setStep("fields")
  }

  const handleFieldsContinue = () => {
    setStep("review")
  }

  const handleReviewBack = () => {
    setStep("fields")
  }

  const handleFieldsBack = () => {
    if (lockLane) {
      setOpen(false)
    } else {
      setStep("lane")
    }
  }

  const handleSubmit = async () => {
    if (!lane || !currentFields) return
    setSubmitting(true)
    try {
      // Upload the auto-captured screenshot on demand. Skip if the user
      // unchecked the include option, or if there's already an uploaded
      // blob_path (dialog was submitted once and failed — reuse).
      let effectiveFields = currentFields
      if (
        (lane === "bug" || lane === "incident") &&
        includeScreenshot &&
        screenshot &&
        !screenshot.uploadedBlobPath
      ) {
        try {
          const file = new File([screenshot.blob], `screen-${Date.now()}.jpg`, {
            type: "image/jpeg",
          })
          const uploaded = await uploadFeedbackEvidence(file)
          setScreenshot((prev) => (prev ? { ...prev, uploadedBlobPath: uploaded.blob_path } : prev))
          effectiveFields = {
            ...effectiveFields,
            screenshotBlobPath: uploaded.blob_path,
          }
        } catch (err) {
          // Non-fatal — keep submitting without the screenshot rather than
          // blocking the whole report.
          // eslint-disable-next-line no-console
          console.warn("[feedback] screenshot upload failed, submitting without it", err)
        }
      }

      const result = await submitFeedback({
        lane,
        fields: effectiveFields,
        context: apiContext,
        escalate_requested: escalationRequested,
      })
      setSubmitResult(result)
      setStep("done")
    } catch (e) {
      toast.error(
        t(
          "feedback.errors.submitFailed",
          "Could not submit feedback. Please try again or contact support directly."
        )
      )
      // eslint-disable-next-line no-console
      console.error("Feedback submit failed", e)
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => setOpen(false)

  const handleMinimize = () => {
    setMinimized(true)
    setOpen(false)
  }

  const handleResume = () => {
    setMinimized(false)
    setOpen(true)
  }

  // Minimise is only useful for the multi-step lanes where the user is likely
  // to leave the dialog to go gather more info. The one-field response
  // feedback lane is too trivial, and the done step has nothing to preserve.
  const canMinimize =
    (lane === "bug" || lane === "incident") && (step === "fields" || step === "review")

  const dialogTitle = useMemo(() => {
    if (step === "done") return t("feedback.dialog.titleDone", "Feedback submitted")
    if (lane === "response_feedback") return t("feedback.dialog.titleResponse", "Send feedback")
    if (lane === "bug") return t("feedback.dialog.titleBug", "Report a bug")
    if (lane === "incident") return t("feedback.dialog.titleIncident", "Report an incident")
    return t("feedback.dialog.title", "Send feedback")
  }, [lane, step, t])

  const dialogDescription = useMemo(() => {
    if (step === "lane") {
      return t(
        "feedback.dialog.lanePickerDescription",
        "Pick the type of feedback that best matches what you want to tell us."
      )
    }
    if (step === "review") {
      return t(
        "feedback.dialog.reviewDescription",
        "Review what we'll send before submitting. Nothing is sent until you click submit."
      )
    }
    return undefined
  }, [step, t])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          {trigger ?? (
            <button
              className="border-border hover:bg-accent/40 inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs"
              type="button"
            >
              <MessageSquare className="size-3.5" />
              {t("feedback.dialog.openButton", "Send feedback")}
            </button>
          )}
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          {dialogDescription ? <DialogDescription>{dialogDescription}</DialogDescription> : null}
          {canMinimize ? (
            <button
              aria-label={t("feedback.dialog.minimize", "Minimize")}
              className="ring-offset-background focus:ring-ring absolute top-4 right-12 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none"
              type="button"
              title={t(
                "feedback.dialog.minimizeTooltip",
                "Minimize — your draft is kept so you can grab screenshots and resume"
              )}
              onClick={handleMinimize}
            >
              <Minimize2 className="size-4" />
              <span className="sr-only">{t("feedback.dialog.minimize", "Minimize")}</span>
            </button>
          ) : null}
        </DialogHeader>
        <DialogBody className="space-y-4">
          {step === "lane" && <LanePicker onSelect={handleLaneSelect} />}

          {step === "fields" && lane === "response_feedback" && (
            <ResponseFeedbackStep
              hideBack={lockLane && !!initialLane}
              initialFields={responseFeedback}
              submitting={submitting}
              values={responseFeedback}
              onBack={handleFieldsBack}
              onChange={setResponseFeedback}
              onSubmit={handleFieldsContinue}
            />
          )}

          {step === "fields" && lane === "bug" && (
            <BugReportStep
              escalationRequested={escalationRequested}
              evidenceSlot={<EvidenceUpload value={evidence} onChange={setEvidence} />}
              submitting={submitting}
              values={bug}
              screenshotSlot={
                <ScreenshotAttachment
                  capturing={screenshot === null && !screenshotSuppressed}
                  included={includeScreenshot}
                  previewUrl={screenshot?.previewUrl ?? null}
                  suppressed={screenshotSuppressed}
                  onToggle={setIncludeScreenshot}
                />
              }
              onBack={handleFieldsBack}
              onChange={setBug}
              onEscalationRequestedChange={setEscalationRequested}
              onSubmit={handleFieldsContinue}
            />
          )}

          {step === "fields" && lane === "incident" && (
            <IncidentStep
              escalationRequested={escalationRequested}
              evidenceSlot={<EvidenceUpload value={evidence} onChange={setEvidence} />}
              submitting={submitting}
              values={incident}
              screenshotSlot={
                <ScreenshotAttachment
                  capturing={screenshot === null && !screenshotSuppressed}
                  included={includeScreenshot}
                  previewUrl={screenshot?.previewUrl ?? null}
                  suppressed={screenshotSuppressed}
                  onToggle={setIncludeScreenshot}
                />
              }
              onBack={handleFieldsBack}
              onChange={setIncident}
              onEscalationRequestedChange={setEscalationRequested}
              onSubmit={handleFieldsContinue}
            />
          )}

          {step === "review" && lane && currentFields && (
            <ReviewStep
              context={apiContext}
              escalationRequested={escalationRequested}
              evidencePreview={evidencePreview}
              fields={currentFields}
              lane={lane}
              screenshotPreview={screenshotPreview}
              submitting={submitting}
              onBack={handleReviewBack}
              onSubmit={handleSubmit}
            />
          )}

          {step === "done" && submitResult && (
            <ConfirmationStep
              fullPayload={{ lane, fields: currentFields, context: apiContext }}
              result={submitResult}
              onClose={handleClose}
            />
          )}
        </DialogBody>
      </DialogContent>
      {minimized ? <ResumeChip lane={lane} onResume={handleResume} /> : null}
    </Dialog>
  )
}
